import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CityToken, CountryRegionToken } from '@sleed/bizcommerce-api';
import { citiesApi, countryRegionsApi } from '~/lib/api/client';
import { formToToken, type CityFormValues } from '~/lib/schemas/city';

export const cityKeys = {
	all: ['cities'] as const,
	list: () => [...cityKeys.all, 'list'] as const,
	regions: () => [...cityKeys.all, 'regions'] as const
};

/** Full city list (legacy `/cities/getall`, loadMode 'raw'). */
export function useCitiesList() {
	return useQuery<CityToken[]>({
		queryKey: cityKeys.list(),
		queryFn: async () => (await citiesApi.getAll()).data.items ?? []
	});
}

/** Region lookup ({id, nm}) for the parent column + edit dropdown. */
export function useCityRegions() {
	return useQuery<CountryRegionToken[]>({
		queryKey: cityKeys.regions(),
		queryFn: async () => (await countryRegionsApi.getAll()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useCityMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: cityKeys.list() });

	const create = useMutation({
		mutationFn: async (form: CityFormValues) => (await citiesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: CityFormValues) => (await citiesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await citiesApi.remove(id)).data,
		onSuccess: invalidate
	});

	return { create, update, remove };
}
